import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  NumberField,
  PrecomputeFormulas,
  TextCollectionFieldType,
} from '@scale/shared/types/params/textcollection';
import {
  weighted_average,
  weighted_average_diff,
} from '@scale/shared/types/params/numberFieldFormulas';
import {
  NumberFieldAnnotation,
  TextCollectionAnnotation,
} from '@scale/shared/types/response/textcollection';
import { SegmentedButtons, Typography } from '@scale/scaleui';
import { twMerge } from 'tailwind-merge';
import FieldHeader from 'react-scale/components/scalers/tasks/textcollection/SimpleTextCollection/FieldHeader';

interface SliderFieldSectionProps {
  field: NumberField;
  annotation: NumberFieldAnnotation;
  setAnnotation: (annotation: NumberFieldAnnotation) => void;
  currentAnnotations?: Record<string, TextCollectionAnnotation>;
  disabled?: boolean;
}

const ScoreSelectionFieldSection = (props: SliderFieldSectionProps) => {
  const onChange = function (index: number, value: string): void {
    const response = props.annotation.response.slice();
    response[index] = value;
    const annotation: NumberFieldAnnotation = {
      ...props.annotation,
      response,
    };
    props.setAnnotation(annotation);
  };

  const [precomputeDefaultValue, setPrecomputeDefaultValue] = useState<number | undefined>(
    undefined,
  );

  const validPrecomputeProps = useCallback((props: SliderFieldSectionProps) => {
    if (
      !props.currentAnnotations ||
      !props.field.precompute_field_params ||
      !props.field.precompute_field_params.weights ||
      !props.field.precompute_field_params.precompute_formula ||
      !props.field.min ||
      !props.field.max
    )
      return false;

    const precomputeFormula = props.field.precompute_field_params.precompute_formula;
    const weightsMapList = props.field.precompute_field_params.weights;
    if (precomputeFormula === PrecomputeFormulas.WEIGHTED_AVERAGE && weightsMapList.length !== 1) {
      return false;
    } else if (precomputeFormula === PrecomputeFormulas.WEIGHTED_AVERAGE_DIFF) {
      if (weightsMapList.length !== 2) return false;
      if (!props.field.precompute_field_params.thresholds) return false;
    }
    return true;
  }, []);

  useEffect(() => {
    if (!validPrecomputeProps(props)) return;
    const precomputeFieldParams = props.field.precompute_field_params;
    const precomputeFormula = precomputeFieldParams?.precompute_formula;
    const weightsMapList = precomputeFieldParams?.weights!;
    const thresholds = precomputeFieldParams?.thresholds;

    const valuesList = [];
    const weightsList = [];

    for (const fieldWeightsMap of weightsMapList) {
      const values = [];
      const weights = [];
      for (const [fieldId, weight] of Object.entries(fieldWeightsMap)) {
        const annotation = props.currentAnnotations![fieldId];
        if (
          !annotation ||
          !annotation.response ||
          annotation.type !== TextCollectionFieldType.Number
        ) {
          return;
        }
        values.push(+annotation.response[0]);
        weights.push(weight);
      }
      valuesList.push(values);
      weightsList.push(weights);
    }

    if (precomputeFormula === PrecomputeFormulas.WEIGHTED_AVERAGE) {
      setPrecomputeDefaultValue(weighted_average(weightsList[0], valuesList[0]));
    } else if (precomputeFormula === PrecomputeFormulas.WEIGHTED_AVERAGE_DIFF) {
      setPrecomputeDefaultValue(
        weighted_average_diff({
          weightsA: weightsList[0],
          valuesA: valuesList[0],
          weightsB: weightsList[1],
          valuesB: valuesList[1],
          thresholds: thresholds!,
          minVal: props.field.min!,
          maxVal: props.field.max!,
        }),
      );
    }
  }, [
    precomputeDefaultValue,
    props,
    props.currentAnnotations,
    props.field.max,
    props.field.min,
    props.field.precompute_field_params,
    validPrecomputeProps,
  ]);

  const values = useMemo(() => {
    const { max, min: minProp, step: stepProp } = props.field;
    const min = minProp || 0;
    // set set to default to 1 if it's less than 0
    const step = (stepProp || 1) > 0 ? stepProp || 1 : 1;

    if (!max) return [];

    const limit = Math.ceil((max - min) / step) + 1;

    let value = min;
    const values = Array(limit)
      .fill(null)
      .map((e, i) => {
        if (i === 0) return min;
        if (i === limit - 1) return max;
        value += step;
        return value;
      });

    return values || [];
  }, [props.field]);

  const labels = useMemo(() => {
    const labelsOverride = props.field.labels_list_override?.split(',').map(a => a.trim());
    const { suffix = '', prefix = '', mid_label = '' } = props.field;
    return [...(labelsOverride?.length ? labelsOverride : [prefix, mid_label, suffix])];
  }, [props.field]);

  return (
    <div>
      {props.annotation.response.map((response: string, index: number) => {
        const selectedValue = !response
          ? props.field.default_value !== undefined
            ? props.field.default_value
            : validPrecomputeProps(props)
            ? precomputeDefaultValue?.toString() || props.field.min?.toString() || ''
            : ''
          : response;

        return (
          <div key={`response-${index}`} className="flex flex-col gap-2">
            <FieldHeader
              title={props.field.title}
              description={props.field.description}
              hint={props.field.hint}
              rubric={props.field.rubric}
            />
            <div key={index} className="flex flex-row align-center">
              <div className="my-1 mx-2 pb-4 grow">
                <SegmentedButtons
                  className={twMerge(
                    'scaleui scaleui-tailwind w-full flex flex-row [&>button.selected]:bg-primary-50 [&>button.selected]:ring-primary-500 [&>button:hover]:bg-primary-50',
                    values.length === labels?.length ? 'justify-around' : 'justify-between',
                  )}
                  disabled={props.disabled} // TODO: implement disabled state in scaleui component
                  selected={selectedValue}
                  onSelect={value => {
                    if (props.disabled) return;
                    onChange(index, value.toString());
                  }}
                  buttonClassName={twMerge('w-full', values.length > 15 && 'px-1')}
                  // @ts-expect-error TODO: update SegmentedButtons type
                  buttons={values?.map(value => {
                    return {
                      id: value.toString(),
                      label: (
                        <div className="flex flex-col flex-1 self-start">
                          <Typography.Subtitle3>{value.toString()}</Typography.Subtitle3>
                        </div>
                      ),
                      'data-testid': `score-selection-${value}`,
                    };
                  })}
                />
                <div
                  className={twMerge(
                    'flex mt-1',
                    labels.length === values.length ? 'justify-around' : 'px-2',
                  )}
                >
                  {labels.length > 0 &&
                    labels.map((label, labelindex) => {
                      let textAlign = 'text-center';
                      if (labels.length !== values.length) {
                        if (labelindex === 0) textAlign = 'text-start';
                        if (labelindex === labels.length - 1) textAlign = 'text-end';
                      }
                      return (
                        <div
                          className={twMerge('flex-1', textAlign)}
                          key={`${index}_label_${labelindex}`}
                        >
                          <Typography.Subtitle4>{label}</Typography.Subtitle4>
                        </div>
                      );
                    })}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ScoreSelectionFieldSection;
